/**
 * The module the shell is currently scoped to.
 *
 * Normally that is just `moduleForPath(pathname)` — routes are flat, so the URL
 * is the source of truth for context (moduleMetadata.MODULE_ROUTE_PREFIXES).
 *
 * The exception is the System links (Settings / Feature Access / Workspace /
 * Security). They own no module, yet every module sidebar shows them, so
 * deriving context from the path alone meant clicking Settings inside Finance
 * dropped you back into People: sidebar re-scoped, badge changed, launcher
 * moved its tick. This hook carries the last real module across those paths, so
 * they render inside whatever module you came from, and read as People only
 * when People is where you were.
 *
 * A ref written during render, not state in an effect: state would paint one
 * frame of the wrong sidebar on every Settings click. The write is idempotent
 * for a given path, so a StrictMode double render lands on the same value.
 *
 * Each caller keeps its own ref. They stay in agreement because they all mount
 * for the life of the shell and all re-render on every location change — the
 * one case they share is a fresh load *directly* onto /settings, where no
 * module has been visited yet and every caller correctly says People.
 */
import React from "react";
import { useLocation } from "react-router-dom";
import { ModuleKey, isModuleNeutralPath, moduleForPath } from "./moduleMetadata";

export function useActiveModule(): ModuleKey | null {
  const location = useLocation();
  const owner = moduleForPath(location.pathname);
  const lastRealModule = React.useRef<ModuleKey | null>(null);

  if (!isModuleNeutralPath(location.pathname)) {
    lastRealModule.current = owner;
    return owner;
  }
  return lastRealModule.current;
}
